"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button, Input, PasswordInput, Label, FieldHint, Alert } from "@/components/ui";
import { loginSchema } from "@/schemas/auth";
import { apiPost, ApiClientError } from "@/lib/api-client";
import type { SafeUser } from "@/types/auth";

export function LoginForm({ next, onSuccess }: { next?: string; onSuccess?: (user: SafeUser) => void }) {
  const router = useRouter();
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setErrors({});

    const form = new FormData(e.currentTarget);
    const raw = {
      email: String(form.get("email") ?? ""),
      password: String(form.get("password") ?? ""),
    };

    const parsed = loginSchema.safeParse(raw);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setSubmitting(true);
    try {
      const { user } = await apiPost<{ user: SafeUser }>("/api/auth/login", parsed.data);
      if (onSuccess) {
        onSuccess(user);
        router.refresh();
      } else {
        router.push(next || "/bang-dieu-khien");
        router.refresh();
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFormError(err.message);
      } else {
        setFormError("Không thể kết nối máy chủ. Vui lòng thử lại.");
      }
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {formError && <Alert variant="danger">{formError}</Alert>}

      <div>
        <Label htmlFor="email" required>
          Email
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="ban@example.com"
          invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? "email-error" : undefined}
          className="mt-2"
        />
        {errors.email && (
          <FieldHint id="email-error" error>
            {errors.email}
          </FieldHint>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="password" required>
            Mật khẩu
          </Label>
          <Link href="/quen-mat-khau" className="text-body-sm text-primary hover:underline">
            Quên mật khẩu?
          </Link>
        </div>
        <PasswordInput
          id="password"
          name="password"
          autoComplete="current-password"
          placeholder="Nhập mật khẩu"
          invalid={Boolean(errors.password)}
          aria-describedby={errors.password ? "password-error" : undefined}
          className="mt-2"
        />
        {errors.password && (
          <FieldHint id="password-error" error>
            {errors.password}
          </FieldHint>
        )}
      </div>

      <Button type="submit" size="lg" block isLoading={submitting}>
        Đăng nhập
      </Button>
    </form>
  );
}
